import React, { useEffect, useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Divider,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Fastfood, InfoOutlined, Moped } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { RootReducer } from '@store/index';
import { BranchOffice } from '@api/BranchOffices/BranchOffice';
import BranchOfficeService from '@api/BranchOffices/BranchOfficeServ';
import { setBranchOffices } from '@store/krispisStore';
import { setBranchOfficeSelected } from '@store/authStore';
import { showLoader, hideLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import { showAlertMessage } from '@utils/Alerts/Message';
import IOSSwitch from '@components/Utilities/Switch/IOSSwitch';
import { ModalConfirmDeactivation } from '@components/Modals/ModalConfirmDeactivation';
import useOpenModal from '@hooks/useOpenModal';
import NotificationServ from '@api/Notifications/NotificationServ';
import { NotificationType } from '@api/Notifications/Notification';
import NotificationTemplateServ from '@api/NotificationTemplates/NotificationTemplateServ';
import { resolveTemplateTexts } from '@api/NotificationTemplates/NotificationTemplates';
import { BRANCH_LEAD_PROFILES } from '@utils/Permissions/Capabilities';

// ============================================================================
// Acceso rápido a los servicios de la sucursal, desde el sidebar.
//
// Es el control del DÍA A DÍA: cerrar Delivery cuando la cocina se satura o
// apagar Pickup sin navegar a ninguna pantalla. Por eso se queda aquí y NO se
// movió al formulario de Sucursales (decisión de Irving, 2026-08-18).
//
// Convive a propósito con las dos pantallas que editan lo mismo con formulario
// completo: "Sucursales" (admin) y "Mi sucursal" (gerente). Este atajo escribe
// el MISMO documento — si cambias el modelo de servicios, revisa los tres
// lugares. Al APAGAR un servicio se pide motivo (ModalConfirmDeactivation).
//
// Lo ven GERENTE y ADMIN: desde que el admin dejó de cambiarse de perfil para
// trabajar, necesita el mismo atajo sobre la sucursal que tenga seleccionada.
// ============================================================================

const SidebarBranchServices: React.FunctionComponent = () => {
    const { branchOfficeSelected } = useSelector((state: RootReducer) => state.auth);
    const { branchOffices } = useSelector((state: RootReducer) => state.krispis);
    const dispatch = useDispatch();

    const [servicesConfig, setServicesConfig] = useState({
        hasPickup: false,
        hasDelivery: false,
        deactivationReason: '',
        delivery: {
            initialFee: 0,
            costPerKm: 0,
            maxDistance: 0,
            freeDeliveryEnabled: false,
            minAmountForFreeDelivery: 0
        }
    });

    const [isEditing, setIsEditing] = useState(false);
    const [pendingDeactivation, setPendingDeactivation] = useState<'pickup' | 'delivery' | null>(null);

    const deactivationModal = useOpenModal();

    // Cargar configuración actual cuando cambia la sucursal seleccionada
    useEffect(() => {
        if (branchOfficeSelected) {
            setServicesConfig({
                hasPickup: branchOfficeSelected.hasPickup || false,
                hasDelivery: branchOfficeSelected.hasDelivery || false,
                deactivationReason: branchOfficeSelected.deactivationReason || '',
                delivery: {
                    initialFee: branchOfficeSelected.delivery?.initialFee || 0,
                    costPerKm: branchOfficeSelected.delivery?.costPerKm || 0,
                    maxDistance: branchOfficeSelected.delivery?.maxDistance || 0,
                    freeDeliveryEnabled: branchOfficeSelected.delivery?.freeDeliveryEnabled || false,
                    minAmountForFreeDelivery: branchOfficeSelected.delivery?.minAmountForFreeDelivery || 0
                }
            });
            setIsEditing(false);
        }
    }, [branchOfficeSelected]);

    const handleServiceToggle = (service: 'hasPickup' | 'hasDelivery') => {
        if (!isEditing) setIsEditing(true);
        setServicesConfig((prev) => ({
            ...prev,
            [service]: !prev[service]
        }));
    };

    const handleDeactivationConfirm = (reason: string) => {
        if (!pendingDeactivation) return;

        const reasonField = 'deactivationReason';

        setServicesConfig((prev) => ({
            ...prev,
            [reasonField]: reason
        }));
        setPendingDeactivation(null);

        // After setting the reason, proceed with save
        setTimeout(() => {
            void executeSave(reason);
        }, 100);
    };

    const executeSave = async (reason?: string) => {
        if (!branchOfficeSelected?.id) return;

        showLoader();
        try {
            const updatedBranchOffice: BranchOffice = {
                ...branchOfficeSelected,
                hasPickup: servicesConfig.hasPickup,
                hasDelivery: servicesConfig.hasDelivery,
                // ⚠️ **El motivo se BORRA cuando los dos servicios vuelven a estar
                // encendidos.** Antes se quedaba pegado para siempre: medido el
                // 2026-09-13, **7 de las 10 sucursales estaban abiertas y con un
                // motivo de apagado viejo** ("Cerrado", "Falta de insumos", "KK").
                // Mostrar ese texto sería mostrar una razón falsa — y es justo lo
                // que hacía falta arreglar ANTES de ponerlo a la vista.
                deactivationReason:
                    servicesConfig.hasPickup && servicesConfig.hasDelivery
                        ? undefined
                        : reason || servicesConfig.deactivationReason || undefined,
                delivery: {
                    initialFee: servicesConfig.delivery.initialFee,
                    costPerKm: servicesConfig.delivery.costPerKm,
                    maxDistance: servicesConfig.delivery.maxDistance,
                    freeDeliveryEnabled: servicesConfig.delivery.freeDeliveryEnabled,
                    minAmountForFreeDelivery: servicesConfig.delivery.minAmountForFreeDelivery
                }
            };
            await BranchOfficeService.saveBranchOffice(updatedBranchOffice);

            // Aviso interno al APAGAR un servicio, con el motivo.
            //
            // Hasta hoy el motivo solo quedaba en la bitácora de la sucursal, así
            // que quien no fuera a buscarlo nunca se enteraba de que una sucursal
            // había cerrado delivery ni por qué (pregunta de Irving, 2026-09-13).
            // Va a admin y gerente de ESA sucursal, no a toda la cadena.
            //
            // ⚠️ Solo cuando el servicio pasa de encendido a APAGADO: sin esa
            // comparación, cada guardado de tarifas volvería a avisar de un
            // servicio que lleva días cerrado.
            const apagados: string[] = [];
            if (branchOfficeSelected.hasPickup && !servicesConfig.hasPickup) apagados.push('Pickup');
            if (branchOfficeSelected.hasDelivery && !servicesConfig.hasDelivery) apagados.push('Delivery');
            if (apagados.length) {
                const overrides = await NotificationTemplateServ.getOverrides();
                const motivo = reason || servicesConfig.deactivationReason || 'sin motivo';
                apagados.forEach((servicio) => {
                    void NotificationServ.notify({
                        // Texto editable desde el admin (staff_service_off).
                        ...resolveTemplateTexts(
                            'staff_service_off',
                            { servicio, sucursal: branchOfficeSelected.name || 'la sucursal', motivo },
                            overrides
                        ),
                        type: NotificationType.System,
                        audience: {
                            profiles: BRANCH_LEAD_PROFILES,
                            branchOfficeIds: [branchOfficeSelected.id as string]
                        },
                        link: '/branch-offices',
                        // Uno por servicio, sucursal y día: apagar y encender varias
                        // veces la misma tarde no llena la campana.
                        referenceId: `servicio-off-${branchOfficeSelected.id}-${servicio}-${new Date().toLocaleDateString('es-MX')}`
                    });
                });
            }
            const updatedBranchOffices = branchOffices.map((bo) =>
                bo.id === branchOfficeSelected.id ? updatedBranchOffice : bo
            );
            dispatch(setBranchOffices(updatedBranchOffices));
            dispatch(setBranchOfficeSelected(updatedBranchOffice));
            setIsEditing(false);
            showAlertMessage('Configuración guardada exitosamente');
        } catch (error) {
            showAlertError('Error al guardar la configuración: ' + error);
        }
        hideLoader();
    };

    const checkAndSave = () => {
        if (!branchOfficeSelected) return;

        // Check if pickup was deactivated (was true, now false)
        if (branchOfficeSelected.hasPickup && !servicesConfig.hasPickup) {
            setPendingDeactivation('pickup');
            deactivationModal.toggle();
            return;
        }

        // Check if delivery was deactivated (was true, now false)
        if (branchOfficeSelected.hasDelivery && !servicesConfig.hasDelivery) {
            setPendingDeactivation('delivery');
            deactivationModal.toggle();
            return;
        }

        // No deactivation, proceed with save
        void executeSave();
    };

    const handleDeliveryConfigChange = (field: string, value: number | boolean) => {
        if (!isEditing) setIsEditing(true);
        setServicesConfig((prev) => ({
            ...prev,
            delivery: {
                ...prev.delivery,
                [field]: value
            }
        }));
    };

    const handleFreeDeliveryToggle = () => {
        if (!isEditing) setIsEditing(true);
        setServicesConfig((prev) => ({
            ...prev,
            delivery: {
                ...prev.delivery,
                freeDeliveryEnabled: !prev.delivery.freeDeliveryEnabled
            }
        }));
    };

    const saveServicesConfig = () => {
        checkAndSave();
    };

    const cancelEditing = () => {
        if (branchOfficeSelected) {
            setServicesConfig({
                hasPickup: branchOfficeSelected.hasPickup || false,
                hasDelivery: branchOfficeSelected.hasDelivery || false,
                deactivationReason: branchOfficeSelected.deactivationReason || '',
                delivery: {
                    initialFee: branchOfficeSelected.delivery?.initialFee || 0,
                    costPerKm: branchOfficeSelected.delivery?.costPerKm || 0,
                    maxDistance: branchOfficeSelected.delivery?.maxDistance || 0,
                    freeDeliveryEnabled: branchOfficeSelected.delivery?.freeDeliveryEnabled || false,
                    minAmountForFreeDelivery: branchOfficeSelected.delivery?.minAmountForFreeDelivery || 0
                }
            });
        }
        setIsEditing(false);
    };

    if (!branchOfficeSelected) {
        return null;
    }

    return (
        <>
            <Box px={2} pb={2}>
                <Divider sx={{ mb: 1.5 }} />

                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.5}>
                    <Typography variant="overline" fontWeight={700} color="text.secondary">
                        Servicios
                    </Typography>
                    {isEditing && (
                        <Typography variant="caption" color="warning.main" fontWeight={600}>
                            Sin guardar
                        </Typography>
                    )}
                </Stack>

                {/* Los dos interruptores de operación: es lo que se toca a diario */}
                <Stack>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" py={0.5}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <Fastfood fontSize="small" />
                            <Typography variant="body2" fontWeight={600}>
                                Pickup
                            </Typography>
                        </Stack>
                        <IOSSwitch checked={servicesConfig.hasPickup} onChange={() => handleServiceToggle('hasPickup')} />
                    </Stack>

                    <Stack direction="row" alignItems="center" justifyContent="space-between" py={0.5}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <Moped fontSize="small" />
                            <Typography variant="body2" fontWeight={600}>
                                Delivery
                            </Typography>
                        </Stack>
                        <IOSSwitch
                            checked={servicesConfig.hasDelivery}
                            onChange={() => handleServiceToggle('hasDelivery')}
                        />
                    </Stack>
                </Stack>

                {/* El motivo del apagado, A LA VISTA. Antes solo quedaba en la
                    bitácora de la sucursal, así que quien veía el servicio en gris
                    no tenía forma de saber por qué (pregunta de Irving, 2026-09-13).
                    Solo se pinta si de verdad hay algo apagado: con todo encendido
                    el motivo ya no existe. */}
                {!!servicesConfig.deactivationReason &&
                    (!servicesConfig.hasPickup || !servicesConfig.hasDelivery) && (
                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="flex-start"
                            sx={{ bgcolor: 'warning.light', borderRadius: 1, px: 1, py: 0.75, mt: 0.5 }}
                        >
                            <InfoOutlined fontSize="small" sx={{ color: 'warning.dark', mt: 0.2 }} />
                            <Typography variant="caption" color="warning.dark" fontWeight={600}>
                                {!servicesConfig.hasPickup && !servicesConfig.hasDelivery
                                    ? 'Pickup y Delivery apagados'
                                    : `${!servicesConfig.hasPickup ? 'Pickup' : 'Delivery'} apagado`}
                                : {servicesConfig.deactivationReason}
                            </Typography>
                        </Stack>
                    )}

                {/* Tarifas: plegadas por defecto y en DOS COLUMNAS — son campos
                    numéricos cortos, a renglón completo desperdiciaban el alto
                    del menú y empujaban el guardar fuera de la vista */}
                {servicesConfig.hasDelivery && (
                    <Accordion
                        disableGutters
                        elevation={0}
                        sx={{ bgcolor: 'transparent', '&:before': { display: 'none' } }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon fontSize="small" />}
                            sx={{ px: 0, minHeight: 36, '& .MuiAccordionSummary-content': { my: 0.5 } }}
                        >
                            <Typography variant="caption" fontWeight={700} color="text.secondary">
                                Tarifas de envío
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ px: 0, pt: 0 }}>
                            <Stack spacing={1.5}>
                                <Stack direction="row" spacing={1}>
                                    <TextField
                                        size="small"
                                        label="Tarifa inicial"
                                        type="number"
                                        value={servicesConfig.delivery.initialFee}
                                        onChange={(e) =>
                                            handleDeliveryConfigChange('initialFee', parseFloat(e.target.value) || 0)
                                        }
                                        InputProps={{ startAdornment: <Typography mr={0.5}>$</Typography> }}
                                    />
                                    <TextField
                                        size="small"
                                        label="Por km"
                                        type="number"
                                        value={servicesConfig.delivery.costPerKm}
                                        onChange={(e) =>
                                            handleDeliveryConfigChange('costPerKm', parseFloat(e.target.value) || 0)
                                        }
                                        InputProps={{ startAdornment: <Typography mr={0.5}>$</Typography> }}
                                    />
                                </Stack>

                                <TextField
                                    size="small"
                                    label="Distancia máxima"
                                    type="number"
                                    value={servicesConfig.delivery.maxDistance}
                                    onChange={(e) =>
                                        handleDeliveryConfigChange('maxDistance', parseFloat(e.target.value) || 0)
                                    }
                                    InputProps={{ endAdornment: <Typography ml={0.5}>km</Typography> }}
                                />

                                {/* Antes era un Checkbox con etiqueta larga: rompía
                                    la lectura con los switches de arriba */}
                                <Stack direction="row" alignItems="center" justifyContent="space-between">
                                    <Typography variant="body2" fontWeight={600}>
                                        Envío gratis
                                    </Typography>
                                    <IOSSwitch
                                        checked={servicesConfig.delivery.freeDeliveryEnabled}
                                        onChange={handleFreeDeliveryToggle}
                                    />
                                </Stack>

                                {servicesConfig.delivery.freeDeliveryEnabled && (
                                    <TextField
                                        size="small"
                                        label="Desde"
                                        type="number"
                                        value={servicesConfig.delivery.minAmountForFreeDelivery}
                                        onChange={(e) =>
                                            handleDeliveryConfigChange(
                                                'minAmountForFreeDelivery',
                                                parseFloat(e.target.value) || 0
                                            )
                                        }
                                        placeholder="Ej. 200"
                                        InputProps={{ startAdornment: <Typography mr={0.5}>$</Typography> }}
                                        helperText="Monto mínimo del pedido para no cobrar envío"
                                    />
                                )}
                            </Stack>
                        </AccordionDetails>
                    </Accordion>
                )}

                {/* Solo aparecen si hay cambios, en UN renglón: antes eran dos
                    botones a todo lo ancho, apilados, siempre presentes */}
                {isEditing && (
                    <Stack direction="row" spacing={1} justifyContent="flex-end" mt={1.5}>
                        <Button size="small" color="inherit" onClick={cancelEditing}>
                            Cancelar
                        </Button>
                        <Button size="small" variant="contained" onClick={saveServicesConfig}>
                            Guardar
                        </Button>
                    </Stack>
                )}
            </Box>

            {pendingDeactivation && (
                <ModalConfirmDeactivation
                    modal={deactivationModal}
                    serviceType={pendingDeactivation}
                    onConfirm={handleDeactivationConfirm}
                />
            )}
        </>
    );
};

export default SidebarBranchServices;
